import {
  Component, EventEmitter, Inject, OnDestroy, OnInit, Output, PLATFORM_ID,
  Input,
  Renderer2,
  ViewChild
} from '@angular/core';
import {FileUpload, FileUploadModule} from "primeng/fileupload";
import {FilesUploadEventInterface} from "../../interfaces/files-upload-event.interface";
import {FileRemoveEventInterface} from "../../interfaces/file-remove-event.interface";
import {MediaObjectApiModel} from "../../../api/models/media-object.api-model";
import {defer, forkJoin, Observable, of} from "rxjs";
import {finalize, map, mergeMap, tap} from "rxjs/operators";
import {MediaObjectRestService} from "../../../api/services/media-object.rest.service";
import {TranslateModule} from "@ngx-translate/core";
import {isPlatformBrowser, NgIf, NgFor} from "@angular/common";
import {Ripple} from "primeng/ripple";
import {ModalService} from "../../modal/modal.service";
import {CurrentUserViewModel} from "../../../core/models/current-user.view-model";
import {ProjectViewModel} from "../../../features/user/garage/project.view-model";
import {ConfirmDialogModule} from "primeng/confirmdialog";
import {DialogModule} from "primeng/dialog";
import {MediaModule} from "../media.module";
import {MediaChoiceComponent} from "../../modal/components/media-choice/media-choice.component";
import {FileRestService} from "../../../api/services/file.rest.service";
import {MediaObjectPipe} from "../../../core/pipes/media-object.pipe";
import {MediaObjectInterface} from "../../../core/interfaces/media-object.interface";


import {DomSanitizer} from '@angular/platform-browser';
import {releaseFileUploadPreview} from './file-upload-preview';
import {ButtonModule} from 'primeng/button';
import {ImageCropDialogComponent} from '../../image-cropper/image-crop-dialog.component';
import {ImageCropResult} from '../../image-cropper/image-crop-state';
import {MediaSelection, MediaSelectionEntry, PreparedMediaSelection} from './media-selection';
import {MediaUploadService} from '../../../core/services/media-upload.service';

@Component({
  selector: 'app-media-uploader',
  standalone: true,
  imports: [
    FileUploadModule, ButtonModule, NgFor, ImageCropDialogComponent,
    TranslateModule,
    NgIf,
    Ripple,
    ConfirmDialogModule,
    DialogModule,
    MediaModule,
    MediaChoiceComponent
  ],
  templateUrl: './media-uploader.component.html',
  styleUrl: './media-uploader.component.scss'
})
export class MediaUploaderComponent implements OnInit, OnDestroy {
  get mediaObjectAMs(): MediaObjectInterface[] {
    return this.cropEnabled ? this.selection.entries.filter(e => !!e.source).map(e => e.source!) : this._mediaObjectAMs;
  }

  @Input() set mediaObjectAMs(value: MediaObjectInterface[]) {
    this._mediaObjectAMs = value;
  }
  @Input() cropEnabled = false;
  @Input() locked = false;
  @Output() busyChange = new EventEmitter<boolean>();
  readonly selection = new MediaSelection();
  cropEntry?: MediaSelectionEntry;
  uploading = false;
  private selectionInitialized = false;
  get busy(): boolean { return !!this.cropEntry || this.uploading || this.mediaDialogOpened; }
  public gallerySelection: MediaObjectInterface[] = [];

  @Input() public currentUserVM: CurrentUserViewModel | null = null;
  @Input() public projectVM: ProjectViewModel | null = null;
  @Input() public allowFromGallery: boolean = true;
  @ViewChild(MediaChoiceComponent, {static: false}) mediaChoice!: MediaChoiceComponent;
  @ViewChild(FileUpload) fileUploader: FileUpload;

  public image: any;
  public objectURL = '';
  public files: File[] = [];
  public newFiles: File[] = [];
  public disabled = false;
  public mediaProgress: number | null = null;
  public mediaChoiceOpened: boolean = false;
  public mediaDialogOpened: boolean = false;
  private syncingGallerySelection: boolean = false;
  private _mediaObjectAMs: MediaObjectInterface[] = [];

  constructor(
    private readonly renderer: Renderer2,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly ms: ModalService,
    private readonly frs: FileRestService,
    private readonly uploadService: MediaUploadService,
    private readonly sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private readonly platform: object,
  ) {
  }

  ngOnInit(): void {
    if (this.cropEnabled) { this.initializeMedia(this._mediaObjectAMs); }
  }

  initializeMedia(media: MediaObjectInterface[]): void {
    if (!this.cropEnabled) { this._mediaObjectAMs = media; return; }
    this.selection.selectSources(media || []);
    this.selectionInitialized = true;
    this.syncFiles();
  }

  chooseFiles(): void {
    if (!isPlatformBrowser(this.platform) || this.locked || this.busy || this.disabled) { return; }
    this.fileUploader.choose();
  }

  chooseMedia(): void {
    if (this.locked || this.busy || this.disabled) { return; }
    if (this.allowFromGallery) { this.mediaChoiceOpened = true; }
    else { this.chooseFiles(); }
  }

  addFiles(event: {files: File[]}, uploader: FileUpload): void {
    if (!isPlatformBrowser(this.platform) || this.locked || this.busy) { return; }
    for (const file of event.files) {
      releaseFileUploadPreview(file, this.sanitizer);
      if (!file.type.startsWith('image/')) { continue; }
      const entry = this.selection.addFile(file);
      this.updatePreview(entry, file);
    }
    uploader.clear();
    this.syncFiles();
  }

  editEntry(entry: MediaSelectionEntry): void {
    if (this.locked || this.busy || !isPlatformBrowser(this.platform)) { return; }
    this.cropEntry = entry;
    this.busyChange.emit(true);
  }

  acceptCrop(result: ImageCropResult): void {
    if (!this.cropEntry) { return; }
    this.selection.crop(this.cropEntry, result.file, result.source, result.state);
    this.updatePreview(this.cropEntry, result.file);
    this.syncFiles();
    this.closeCrop();
  }

  closeCrop(): void {
    this.cropEntry = undefined;
    this.busyChange.emit(this.busy);
  }

  removeEntry(entry: MediaSelectionEntry): void {
    if (this.locked || this.busy) { return; }
    this.releasePreview(entry);
    this.selection.remove(entry);
    this.syncFiles();
  }

  entryPreview(entry: MediaSelectionEntry): string {
    return entry.preview || MediaObjectPipe.transformToLink(entry.source, {formatType: 'thumb_md', defaultImageType: 'car'});
  }

  canCrop(entry: MediaSelectionEntry): boolean {
    return !!entry.file || entry.source?.type === 'IMAGE' || !!entry.source?.mimeType?.startsWith('image/')
      || (!!entry.source?.['@id'] && !entry.source.type && !entry.source.mimeType);
  }

  onMediaDialogClosed(): void { this.busyChange.emit(this.busy); }

  trackEntry(_index: number, entry: MediaSelectionEntry): number { return entry.id; }

  /** Upload each changed entry once; retained results make a failed form save retryable. */
  prepareMedia(progress: (value: number) => void): Observable<PreparedMediaSelection> {
    return defer(() => {
      this.uploading = true;
      this.busyChange.emit(true);
      const pending = this.selection.entries.filter(entry => entry.file && !entry.uploaded);
      const values = pending.map(() => 0);
      const requests = pending.map((entry, index) => this.uploadService.uploadMedia([entry.file!]).pipe(
        mergeMap(({mediaObjects$, progress$}) => {
          const subscription = progress$.subscribe(value => {
            values[index] = value;
            progress(values.reduce((sum, item) => sum + item, 0) / values.length);
          });
          return mediaObjects$.pipe(
            tap(media => entry.uploaded = media[0]),
            finalize(() => subscription.unsubscribe()),
          );
        }),
      ));
      return (requests.length ? forkJoin(requests) : of([])).pipe(
        map(() => this.selection.prepared()),
        finalize(() => { this.uploading = false; this.busyChange.emit(this.busy); }),
      );
    });
  }

  private syncFiles(): void {
    this.files = this.selection.entries.map(entry => entry.file || entry.sourceFile).filter(Boolean) as File[];
    this.newFiles = this.selection.entries.filter(entry => entry.file && !entry.uploaded).map(entry => entry.file!);
  }

  private updatePreview(entry: MediaSelectionEntry, file: File): void {
    this.releasePreview(entry);
    if (isPlatformBrowser(this.platform)) { entry.preview = URL.createObjectURL(file); }
  }

  private releasePreview(entry: MediaSelectionEntry): void {
    if (entry.preview && isPlatformBrowser(this.platform)) { URL.revokeObjectURL(entry.preview); }
    entry.preview = undefined;
  }

  ngOnDestroy(): void { this.selection.entries.forEach(entry => this.releasePreview(entry)); }

/*  ngAfterContentInit(): void {
    this.convertUrlToFile(this.mediaObjectAMs.map(e => e.hyperlinks[0].uri));

    this.selectMedia(this.fileUploader);
  }*/

  onUpload(event: any, type: 'single' | 'multiple') {
    this.openMediaBox();
    switch (type) {
      case 'single':
        const file = event.files[0];
        file.objectURL = file.objectURL ? file.objectURL : this.objectURL;
        if (!file.objectURL) {
          return;
        } else {
          this.image = file;
          this.objectURL = file.objectURL;
        }
        break;
      case 'multiple':
        // this.form.get('images').setValue(event.files);
        break;
    }
  }

  public openMediaBox(): void {
    if (!isPlatformBrowser(this.platform)) { return; }
    setTimeout(() => {
      const elements = document.querySelectorAll('.p-fileupload-row button');
      elements.forEach((e) => {
        this.renderer.addClass(e, 'p-button-text');
      });
    }, 1);
  }

  public onFileSelect(event: FilesUploadEventInterface): void {
    event.files.forEach((file: File) => {
      if (this.syncingGallerySelection) {
        if (!this.files.includes(file)) {
          this.files.push(file);
        }
        return;
      }

      if (!this.files.includes(file)) {
        this.files.push(file);
        this.newFiles.push(file);
      }
    });
  }

  onFileRemove(event: FileRemoveEventInterface): void {
    this.removeMedia(event).subscribe((mediaObjectAM: MediaObjectApiModel) => {
      if (mediaObjectAM) {
        this.mediaObjectAMs = this.mediaObjectAMs.filter(e => e['@id'] !== mediaObjectAM['@id']);
        if (this.mediaChoice) {
          this.mediaChoice.selectedMedia = this.mediaChoice.selectedMedia.filter(e => e['@id'] !== mediaObjectAM['@id']);
        }
      }

      this.files = this.files.filter(e => e.name !== event.file.name);
      this.newFiles = this.newFiles.filter(e => e.name !== event.file.name);
      this.closeMediaLoadProcess();
      this.disabled = false;
    });
  }

  removeMedia(event: FileRemoveEventInterface): Observable<any> {
    this.disabled = true;
    const mediaObjectAM = this.mediaObjectAMs.find(
      (mediaObjectAM: MediaObjectInterface) => mediaObjectAM.name === event.file.name || mediaObjectAM.originalName === event.file.name
    );
    if (mediaObjectAM) {
      return of(mediaObjectAM);
    }
    return of(null);
  }

  public deleteMediaObject(mediaObjectAM: MediaObjectApiModel): Observable<MediaObjectApiModel> {
    return this.mediaObjectRS.deleteMediaObject(mediaObjectAM['@id']).pipe(map(() => mediaObjectAM));
  }

  public closeMediaLoadProcess(): void {
    this.mediaProgress = null;
  }

  public openMediaChoiceModal(): void {
    if (this.currentUserVM && !this.locked && !this.busy) {
      this.gallerySelection = [...this.mediaObjectAMs];
      this.mediaDialogOpened = true;
      this.busyChange.emit(this.busy);
      // this.ms.showMediaChoiceModal({currentUserVM: this.currentUserVM, projectVM: this.projectVM}).successState.subscribe((state: ModalStateEvent) => {
      //   console.log(state)
      // });
    }
  }

  selectMedia(fileUploader: FileUpload): void {
    if (this.cropEnabled) {
      if (this.mediaDialogOpened && this.mediaChoice) {
        const selected = this.mediaChoice.selectedMedia;
        const keep = new Set(selected.map(media => media['@id']));
        this.selection.entries.filter(entry => entry.source && !keep.has(entry.source['@id']))
          .forEach(entry => this.releasePreview(entry));
        this.selection.selectSources(selected);
        this.syncFiles();
      } else if (!this.selectionInitialized) {
        this.initializeMedia(this._mediaObjectAMs);
      }
      this.mediaDialogOpened = false;
      this.busyChange.emit(this.busy);
      return;
    }
    if (this.mediaChoice && this.allowFromGallery) {
      this.mediaObjectAMs = this.mediaChoice.selectedMedia;
    }

    this.files = this.files.filter(f => !![...this.mediaObjectAMs, ...this.newFiles].find(m => m.name === f.name));
    this.syncingGallerySelection = true;
    this.convertUrlToFile(this.mediaObjectAMs.map(e => MediaObjectPipe.transformToLink(e)))
      .pipe(finalize(() => this.syncingGallerySelection = false))
      .subscribe(() => {
        fileUploader.files = this.files;
        fileUploader.upload();
        this.openMediaBox();
        this.mediaDialogOpened = false;
      });

  }

  convertUrlToFile(fileUrls: string[]): Observable<File[]> {
    const requests: Observable<File>[] = [];
    for (const url of fileUrls) {
      // arr.push(new File([await (await fetch(url)).blob()], url, {type: 'image/jpeg'}));
      requests.push(this.frs.getFile(url).pipe(tap(file => {
        if (!this.files.find(f => f.name == file.name)) {
          this.files.push(file);
        }
      })));
    }

    if (!requests.length) {
      this.files = [...this.newFiles];
      return of(this.files);
    }

    return forkJoin(requests);
  }


  private getUniqueFiles(files: File[]): File[] {
    const uniqueFilesSet = new Set<string>();
    const uniqueFiles: File[] = [];

    files.forEach(file => {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
      if (!uniqueFilesSet.has(fileKey)) {
        uniqueFilesSet.add(fileKey);
        uniqueFiles.push(file);
      }
    });

    return uniqueFiles;
  }

  public reset(): void {
    this.mediaChoice?.reset();
  }

  public selectAll(): void {
    this.mediaChoice?.selectAll();
  }
}
